import React from 'react';

import { AttentionCircleFilledIcon } from '@vlabs/icons';
import type { TReferenceType } from '@vlabs/monokit/entities/matcher/types/types';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';

import st from './inputs.module.sass';
import { useSearchPageTranslation } from '../../../i18n';

export interface FaceInputProps {
  referenceType: TReferenceType;
  onReferenceTypeChange: (v: TReferenceType) => void;
  inputError: string;
  inputValue: string;
  onInputChange: (v: string, {
    validationError,
    debounced,
  }: {
    validationError?: string | undefined;
    debounced?: boolean;
  }) => void;
  resetInput: () => void;
}

export function FaceInput({
  referenceType, onReferenceTypeChange, inputError, inputValue, onInputChange, resetInput,
}: FaceInputProps): JSX.Element {
  const { t } = useSearchPageTranslation();

  return (
    <React.Fragment>
      <div className={st.FaceInputTitle}>{t('reference_selector.face_input.title')}</div>
      <div className={st.FaceInputRadioWrapper}>
        <Control.Radio
          checked={referenceType === 'face_external_id'}
          label={t('reference_selector.face_input.labels.external_id')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onReferenceTypeChange(e.target.value as TReferenceType)}
          value="face_external_id"
        />
        <Control.Radio
          checked={referenceType === 'face'}
          label={t('reference_selector.face_input.labels.luna_id')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onReferenceTypeChange(e.target.value as TReferenceType)}
          value="face"
        />
      </div>

      {referenceType === 'face_external_id' && (
        <React.Fragment>
          <Control.Input
            className={st.FaceInputIdInput}
            data-testid="externalId"
            hasError={!!inputError}
            isClearable
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e?.target?.value, {
              debounced: true,
              validationError:
                e?.target?.value?.length > 36
                  ? validate.maxLength(36).message
                  : undefined,
            })}
            onClear={resetInput}
            placeholder={t('reference_selector.face_input.external_id_input.placeholder')}
            value={inputValue}
          />
          {
            !!inputError
            && (
              <div className={st.InputError}>
                <AttentionCircleFilledIcon />
                {inputError}
              </div>
            )
          }
        </React.Fragment>
      )}

      {referenceType === 'face' && (
        <React.Fragment>
          <Control.Input
            className={st.FaceInputIdInput}
            data-testid="faceId"
            hasError={!!inputError}
            isClearable
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e?.target?.value, {
              validationError: validate.uuid()(e?.target?.value),
            })}
            onClear={resetInput}
            placeholder={t('reference_selector.face_input.face_id_input.placeholder')}
            value={inputValue}
          />
          {
            !!inputError
            && (
              <div className={st.InputError}>
                <AttentionCircleFilledIcon />
                {inputError}
              </div>
            )
          }
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
